import {CSSProperties, Fragment, ReactElement, useEffect, useRef, useState} from "react";
import {
  IParentSize, IProduction, IStrandProduction
} from "../../../types";
import {
  formatDate, saveZipAs,
} from "../../../system/utils";
import { CircularProgress, Collapse, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip
} from "@mui/material";
import StyledTableRow from "../../../components/styledTableRow";
import IconButton from "@mui/material/IconButton";
import TableContainer from "../../../components/tableContainer";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { IMillProductDetail } from "../types";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { getPalette } from "../../../configuration/configuration";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TimelineIcon from "@mui/icons-material/Timeline";
import Detail from "../../../components/dialogs/detail";
import ProductTrends from "../components/dialogPanels/productTrends";
import {
  exportFanByBillets,
  exportFanByMachine,
  exportFanByOrder,
  IBilletData, IDataProcessed, IProdAggregate,
  IProductionMachineData
} from "./libraries/qrExports";
import { useAsyncFetch } from "../../../system/useFetch";
import { ICatalogCategory, ICategoryField } from "../../config/types";


interface IProps {
  productionData: IProduction[];
  products: IMillProductDetail[];
  parentSize: IParentSize;
}

interface IDetailDialog {
  open: boolean;
  item: IBilletData | null;
}

interface IBilletsRowComponentProps {
  parentKey: string;
  open: boolean;
  billets: IBilletData[];
}

interface IProductionMachineRowComponentProps {
  data: IProductionMachineData;
}

type StyleFunc = (opacity: number, error?: boolean) => CSSProperties;

const columnList = [ "Speed", "1A", "1B", "2A", "2B", "3A", "3B", "4A", "5A", "5B" ];

const variableMapping: Record<string, string> = {
  "CC1.RM.STELMOR.STR1.SF1A_DRV_UEANA_ACT_SPEED": "1A",
  "CC1.RM.STELMOR.STR1.SF1B_DRV_UEANA_ACT_SPEED": "1B",
  "CC1.RM.STELMOR.STR1.SF2A_DRV_UEANA_ACT_SPEED": "2A",
  "CC1.RM.STELMOR.STR1.SF2B_DRV_UEANA_ACT_SPEED": "2B",
  "CC1.RM.STELMOR.STR1.SF3A_DRV_UEANA_ACT_SPEED": "3A",
  "CC1.RM.STELMOR.STR1.SF3B_DRV_UEANA_ACT_SPEED": "3B",
  "CC1.RM.STELMOR.STR1.SF4A_DRV_UEANA_ACT_SPEED": "4A",
  "CC1.RM.STELMOR.STR1.SF5A_DRV_UEANA_ACT_SPEED": "5A",
  "CC1.RM.STELMOR.STR2.SF1A_DRV_UEANA_ACT_SPEED": "1A",
  "CC1.RM.STELMOR.STR2.SF1B_DRV_UEANA_ACT_SPEED": "1B",
  "CC1.RM.STELMOR.STR2.SF2A_DRV_UEANA_ACT_SPEED": "2A",
  "CC1.RM.STELMOR.STR2.SF2B_DRV_UEANA_ACT_SPEED": "2B",
  "CC1.RM.STELMOR.STR2.SF3A_DRV_UEANA_ACT_SPEED": "3A",
  "CC1.RM.STELMOR.STR2.SF3B_DRV_UEANA_ACT_SPEED": "3B",
  "CC1.RM.STELMOR.STR2.SF4A_DRV_UEANA_ACT_SPEED": "4A",
  "CC1.RM.STELMOR.STR2.SF5A_DRV_UEANA_ACT_SPEED": "5A",
};

const fieldMapping: Record<string, string> = {
  "CC1.RM.STELMOR.STR1.SF1A_DRV_UEANA_ACT_SPEED": "1A",
  "CC1.RM.STELMOR.STR1.SF1B_DRV_UEANA_ACT_SPEED": "1B",
  "CC1.RM.STELMOR.STR1.SF2A_DRV_UEANA_ACT_SPEED": "2A",
  "CC1.RM.STELMOR.STR1.SF2B_DRV_UEANA_ACT_SPEED": "2B",
  "CC1.RM.STELMOR.STR1.SF3A_DRV_UEANA_ACT_SPEED": "3A",
  "CC1.RM.STELMOR.STR1.SF3B_DRV_UEANA_ACT_SPEED": "3B",
  "CC1.RM.STELMOR.STR1.SF4A_DRV_UEANA_ACT_SPEED": "4A",
  "CC1.RM.STELMOR.STR1.SF5A_DRV_UEANA_ACT_SPEED": "5A",
  "CC1.RM.STELMOR.STR2.SF1A_DRV_UEANA_ACT_SPEED": "1A",
  "CC1.RM.STELMOR.STR2.SF1B_DRV_UEANA_ACT_SPEED": "1B",
  "CC1.RM.STELMOR.STR2.SF2A_DRV_UEANA_ACT_SPEED": "2A",
  "CC1.RM.STELMOR.STR2.SF2B_DRV_UEANA_ACT_SPEED": "2B",
  "CC1.RM.STELMOR.STR2.SF3A_DRV_UEANA_ACT_SPEED": "3A",
  "CC1.RM.STELMOR.STR2.SF3B_DRV_UEANA_ACT_SPEED": "3B",
  "CC1.RM.STELMOR.STR2.SF4A_DRV_UEANA_ACT_SPEED": "4A",
  "CC1.RM.STELMOR.STR2.SF5A_DRV_UEANA_ACT_SPEED": "5A"
};


const style00 = (opacity: number, width: number, sx?: CSSProperties): CSSProperties => ({
  width,
  maxWidth: width,
  minWidth: width,
  zIndex: 999,
  position: "sticky",
  backgroundColor: `rgba(156, 170, 177, ${opacity})`,
  ...sx
});

const widthStyle: CSSProperties = {
  width: 80,
  maxWidth: 80,
};

const style01 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(144, 202, 249, ${opacity})`
});

const style02 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(129, 212, 250, ${opacity})`
});

const style03 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(79, 195, 247, ${opacity})`
});

const style04 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(128, 222, 234, ${opacity})`
});

const style05 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(77, 208, 225, ${opacity})`
});

const style06 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(128, 203, 196, ${opacity})`
});

const style07 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(77, 182, 172, ${opacity})`
});

const style08 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(165, 214, 167, ${opacity})`
});

const style09 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(197, 225, 165, ${opacity})`
});

const style10 = (opacity: number, error = false): CSSProperties => ({
  ...widthStyle,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(174, 213, 129, ${opacity})`
});

const styleByColumn: Record<string, StyleFunc> = {
  "Speed": style01,
  "1A": style02,
  "1B": style03,
  "2A": style04,
  "2B": style05,
  "3A": style06,
  "3B": style07,
  "4A": style08,
  "5A": style09,
  "5B": style10,
};

interface IRowProps {
  row: IDataProcessed;
}

interface IColumnData {
  field: string;
  data?: IProdAggregate;
  error: boolean;
}

const specDictionary: Record<string, ICategoryField[]> = {};

// const getRecipeBackground = (recipe: string | null) => recipe && specDictionary[recipe] &&  specDictionary[recipe].length > 0 ? "#424242" : "red";

const recipeSpecExists = (recipe: string | null) => recipe !== null && specDictionary[recipe] !== undefined && specDictionary[recipe].length > 0;

const getColumnRecord = (aggregate: Record<string, IProdAggregate>, field: string): IColumnData => {
  const data = aggregate[field];

  return {
    field,
    data,
    error: data && data.errors.length > 0
  };
};

const generateColumnData = (aggregate: Record<string, IProdAggregate>): IColumnData[] => {
  return columnList.map(c => getColumnRecord(aggregate, c));
};

const evalError = (value: number | null, min: number | null, max: number | null) => {
  if (!value) {
    return null;
  }

  if (min && value < min) {
    return `${value} < ${min}`;
  }

  return max && value > max ? `${value} < ${max}` : null;
};

const evalAvg = (count: number, sum: number) => count ? Math.trunc(sum / count) : null;

const updateAggregate = (
  code: string,
  aggregate:  Record<string, IProdAggregate>,
  billetAggregate: IProdAggregate) => {
  const record = aggregate[code];
  const error = billetAggregate.errors.length > 0 ? billetAggregate.errors[0] : null;

  if (!record) {
    aggregate[code] = {
      min: billetAggregate.min,
      max: billetAggregate.max,
      sum: billetAggregate.sum,
      count: billetAggregate.count,
      specMin: billetAggregate.specMin,
      specMax: billetAggregate.specMax,
      errors: error ? [ error ] : []
    };
  } else {
    record.min = Math.min(record.min, billetAggregate.min);
    record.max = Math.max(record.max, billetAggregate.max);
    record.sum += billetAggregate.sum;
    record.count += billetAggregate.count;
    if (record.specMin !== billetAggregate.specMin) {
      record.specMin = null;
    }
    if (record.specMax !== billetAggregate.specMax) {
      record.specMax = null;
    }
    if (error) {
      record.errors.push(error);
    }
  }
};

const BilletsRowComponent = ({ billets, open, parentKey }: IBilletsRowComponentProps): ReactElement => {
  const [openDetail, setOpenDetail] = useState<IDetailDialog>({ open: false, item: null });
  const onShowCharts = (item: IBilletData) => setOpenDetail({ open: true, item });

  return (
    <>
      <StyledTableRow>
        <TableCell style={{ padding: 0, border: "none" }} colSpan={55} className="table-cell">
          <Collapse in={open} timeout="auto" unmountOnExit={true}>
            <Table stickyHeader={true} size={"small"} >
              <TableHead style={{ backgroundColor: "#b0bec5" }}>
                <TableRow >
                  <TableCell align="center" style={style00(1, 100, { left: 0 })}/>
                  <TableCell align="center" style={style00(1, 100, { left: 100 })}>Recipe</TableCell>
                  <TableCell align="center" style={style00(1, 60, { left: 200 })}>Billet</TableCell>
                  <TableCell align="center" style={style00(1, 80, { left: 260 })}>Length</TableCell>
                  <TableCell align="center" style={style00(1, 80, { left: 340 })}>Pressure</TableCell>
                  <TableCell align="center" style={style00(1, 80, { left: 420 })}>Weight</TableCell>

                  {columnList.map(c => {
                    const keyBase= `${parentKey}-header-${c}`;
                    const sx = styleByColumn[c](.5);

                    return (
                      <Fragment key={keyBase}>
                        <TableCell key={`${keyBase}-min-spec`} align="center" style={sx}>MIN</TableCell>
                        <TableCell key={`${keyBase}-max-spec`} align="center" style={sx}>MAX</TableCell>
                        <TableCell key={`${keyBase}-min`} align="center" style={sx}>MIN</TableCell>
                        <TableCell key={`${keyBase}-max`} align="center" style={sx}>MAX</TableCell>
                        <TableCell key={`${keyBase}-avg`} align="center" style={sx}>AVG</TableCell>
                      </Fragment>);
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                { billets.map(billetData => {
                  const rowKeyBase = `billet-${billetData.productionDetailID}`;
                  const colData = generateColumnData(billetData.aggregateRecord);
                  const validSpec = recipeSpecExists(billetData.recipe);
                  let status = billetData.isCobble || billetData.isCutUp ? 2 : 1;

                  if (status === 1 && !billetData.weight) {
                    status = 0;
                  }

                  return (
                    <StyledTableRow key={`billet-${billetData.productionDetailID}`} hover={true} role="checkbox" tabIndex={-1}>
                      <TableCell align="right" style={style00(.1, 100, { background: "#424242", left: 0 })}>
                        <IconButton size={"small"} onClick={() => onShowCharts(billetData)}>
                          <TimelineIcon/>
                        </IconButton>
                        {status === 0 &&
                          <Tooltip title="Pending">
                            <ErrorOutlineIcon style={{
                              color: getPalette().statusColor.info.background,
                              verticalAlign: "center",
                              marginLeft: "0.5em"
                            }}/>
                          </Tooltip>}
                        {status === 1 &&
                          <Tooltip title="Processed">
                            <CheckCircleOutlineIcon style={{
                              color: getPalette().statusColor.good.background,
                              verticalAlign: "center",
                              marginLeft: "0.5em"
                            }}/>
                          </Tooltip>
                        }
                        {status === 2 &&
                          <Tooltip title={`Rejected:${billetData.isCobble ? " Cobble" : ""} ${billetData.isCutUp ? "Cut up" : ""}`}>
                            <HighlightOffIcon style={{
                              color: getPalette().statusColor.error.background,
                              verticalAlign: "center",
                              marginLeft: "0.5em"
                            }}/>
                          </Tooltip>
                        }
                      </TableCell>
                      <TableCell align="center" style={style00(.1, 100, { background: validSpec ? "#424242" : "red", left: 100 })}>{billetData.recipe}</TableCell>
                      <TableCell align="center" style={style00(.1, 60, { background: "#424242", left: 200 })}>{billetData.billet}</TableCell>
                      <TableCell align="right" style={style00(.1, 80, { background: "#424242", left: 260 })}>{billetData.compactingLength}</TableCell>
                      <TableCell align="right" style={style00(.1, 80, { background: "#424242", left: 340 })}>{billetData.compactingPressure}</TableCell>
                      <TableCell align="right" style={style00(.1, 80, { background: "#424242", left: 420 })}>{billetData.weight}</TableCell>

                      {colData.map(c => {
                        const keyBase = `${rowKeyBase}-${c.field}`;
                        const sx = styleByColumn[c.field](.1, c.error);

                        return (
                          <Fragment key={keyBase}>
                            <TableCell key={`${keyBase}-min-spec`} align="center" style={sx}>
                              {c.data && c.data.specMin && c.data.specMin.toFixed(0)}
                            </TableCell>
                            <TableCell key={`${keyBase}-max-spec`} align="center" style={sx}>
                              {c.data && c.data.specMax && c.data.specMax.toFixed(0)}
                            </TableCell>
                            <TableCell key={`${keyBase}-min`} align="center" style={sx}>
                              {c.data && c.data.min.toFixed(0)}
                            </TableCell>
                            <TableCell key={`${keyBase}-mmax`} align="center" style={sx}>
                              {c.data && c.data.max.toFixed(0)}
                            </TableCell>
                            <TableCell key={`${keyBase}-avg`} align="center" style={sx}>
                              {c.data && c.data.count && evalAvg(c.data.count, c.data.sum)}
                            </TableCell>
                          </Fragment>
                        );
                      })}
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </StyledTableRow>
      {openDetail.open && openDetail.item &&
        <Detail closed={() => setOpenDetail({ open: false, item: null })} fullWidth={true} maxWidth={"lg"}
          title={`Charts - Mill Order ${openDetail.item.millHeat} - ${openDetail.item.machine.name} - Billet ${openDetail.item.billet}`}>
          <ProductTrends productDetail={openDetail.item}/>
        </Detail>
      }
    </>
  );
};

const ProductionMachineRowComponent = ({ data }: IProductionMachineRowComponentProps): ReactElement => {
  const rowKeyBase = `pm-${data.productionMachine.productionMachineID}`;
  const { productionMachine, billets } = data;
  const [open, setOpen] = useState(false);
  const colData = generateColumnData(data.aggregate);

  return (
    <>
      <StyledTableRow hover={true} role="checkbox" tabIndex={-1}>
        <TableCell align="center" style={{ position: "sticky", left: 0, ...style00(.1, 50, { background: "#4b4c4d", left: 0 }) }}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)} >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell style={style00(.1, 100, { background: "#4b4c4d", left: 50 })}>
          {productionMachine.machine.name}
        </TableCell>
        <TableCell style={style00(.1, 150, { background: "#4b4c4d", left: 150 })}>
          {formatDate(productionMachine.shiftHistory.factoryDay)}
        </TableCell>
        <TableCell style={style00(.1, 100, { background: "#4b4c4d", left: 300 })}>
          {productionMachine.shiftHistory.name}
        </TableCell>
        <TableCell style={style00(.1, 100, { background: "#4b4c4d", left: 400 })}>
          {productionMachine.crew.name}
        </TableCell>

        {colData.map(c => {
          const keyBase = `${rowKeyBase}-${c.field}`;
          const sx = styleByColumn[c.field](.3, c.error);

          return (
            <Fragment key={keyBase}>
              <TableCell key={`${keyBase}-min-spec`} align="center" style={sx}>
                {c.data && c.data.specMin && c.data.specMin.toFixed(0)}
              </TableCell>
              <TableCell key={`${keyBase}-max-spec`} align="center" style={sx}>
                {c.data && c.data.specMax && c.data.specMax.toFixed(0)}
              </TableCell>
              <TableCell key={`${keyBase}-min`} align="center" style={sx}>
                {c.data && c.data.min.toFixed(0)}
              </TableCell>
              <TableCell key={`${keyBase}-max`} align="center" style={sx}>
                {c.data && c.data.max.toFixed(0)}
              </TableCell>
              <TableCell key={`${keyBase}-avg`} align="center" style={sx}>
                {c.data && c.data.count && evalAvg(c.data.count, c.data.sum)}
              </TableCell>
            </Fragment>
          );
        })}
      </StyledTableRow>
      <BilletsRowComponent billets={billets} open={open} parentKey={rowKeyBase} />
    </>
  );
};

const RowComponent = ({ row }: IRowProps): ReactElement => {
  const rowKeyBase = `millHeat-${row.millHeat}`;
  const [open, setOpen] = useState(false);
  const data = generateColumnData(row.aggregate);

  return (
    <>
      <StyledTableRow hover={true} role="checkbox" tabIndex={-1}>
        <TableCell align="left" style={style00(.3, 50, { background: "#696c6e", left: 0 })}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)} >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell style={style00(.3, 150, { background: "#696c6e", left: 50 })}>
          {row.millHeat}
        </TableCell>
        <TableCell style={style00(.3, 150, { background: "#696c6e", left: 200 })}>
          {row.heatNumber}
        </TableCell>
        <TableCell style={style00(.3, 150, { background: "#696c6e", left: 350 })}>
          {row.steelCode}
        </TableCell>

        {data.map(c => {
          const keyBase = `${rowKeyBase}-${c.field}`;
          const sx = styleByColumn[c.field](.5, c.error);

          return (
            <Fragment key={keyBase}>
              <TableCell key={`${keyBase}-min-spec`} align="center" style={sx}>
                {c.data && c.data.specMin && c.data.specMin.toFixed(0)}
              </TableCell>
              <TableCell key={`${keyBase}-max-spec`} align="center" style={sx}>
                {c.data && c.data.specMax && c.data.specMax.toFixed(0)}
              </TableCell>
              <TableCell key={`${keyBase}-min`} align="center" style={sx}>
                {c.data && c.data.min.toFixed(0)}
              </TableCell>
              <TableCell key={`${keyBase}-max`} align="center" style={sx}>
                {c.data && c.data.max.toFixed(0)}
              </TableCell>
              <TableCell key={`${keyBase}-avg`} align="center" style={sx}>
                {c.data && c.data.count && evalAvg(c.data.count, c.data.sum)}
              </TableCell>
            </Fragment>
          );
        })}
      </StyledTableRow>
      <StyledTableRow>
        <TableCell style={{ padding: 0, border: "none" }} colSpan={54} className="table-cell">
          <Collapse in={open} timeout="auto" unmountOnExit={true}>
            <Table stickyHeader={true} size={"small"} >
              <TableHead style={{ backgroundColor: "#b0bec5" }}>
                <TableRow >
                  <TableCell align="center" style={style00(1, 50, { left: 0 })}/>
                  <TableCell align="center" style={style00(1, 100, { left: 50 })}>Machine</TableCell>
                  <TableCell align="center" style={style00(1, 150, { left: 150 })}>Factory Day</TableCell>
                  <TableCell align="center" style={style00(1, 100, { left: 300 })}>Shift</TableCell>
                  <TableCell align="center" style={style00(1, 100, { left: 400 })}>Crew</TableCell>

                  {columnList.map(c => {
                    const keyBase = `header-${rowKeyBase}-${c}`;
                    const sx = styleByColumn[c](.5);

                    return (
                      <Fragment key={keyBase}>
                        <TableCell key={`${keyBase}-min-spec`} align="center" style={sx}>MIN</TableCell>
                        <TableCell key={`${keyBase}-max-spec`} align="center" style={sx}>MAX</TableCell>
                        <TableCell key={`${keyBase}-min`} align="center" style={sx}>MIN</TableCell>
                        <TableCell key={`${keyBase}-max`} align="center" style={sx}>MAX</TableCell>
                        <TableCell key={`${keyBase}-avg`} align="center" style={sx}>AVG</TableCell>
                      </Fragment>);
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {row.productionMachines.map(pm => {

                  return (
                    <ProductionMachineRowComponent key={`pm-${pm.productionMachine.productionMachineID}`} data={pm} />
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </StyledTableRow>
    </>
  );
};

const ProductionQRFanTable = ({ productionData, products, parentSize }: IProps): ReactElement => {
  const [ dataProcessed, setDataProcessed ] = useState<IDataProcessed[] | null>(null);
  const [ getCatalogs ] = useAsyncFetch<Record<string, ICatalogCategory[]>>();
  const [ specs, setSpecs ] = useState<Record<string, ICatalogCategory[]>>({});
  const noSpecs = useRef<Record<string, boolean>>({})

  useEffect(() => {
    const process = async  () => {
      const list = new Set<string>();

      for (const billet of products) {
        const catalogName = billet.recipe ?? "UNDEFINED";
        if (!specs[catalogName] && !noSpecs.current[catalogName]) {
          list.add(catalogName);
        }
      }

      if (list.size > 0) {
        const response = await getCatalogs({
          module: "catalog",
          query: "getCatalogsFromName",
          payload: {
            areaCode: "MILL",
            zoneCode: "WIREROD",
            catalogNames: [ ...list ]
          }
        });

        list.forEach(catalogName => {
          noSpecs.current[catalogName] = !(response && response[catalogName] && response[catalogName].length > 0);
        });

        setSpecs(prevState => ({ ...prevState, ...response }));
      }
    };

    process().then();
  }, [products, specs]);

  useEffect(() => {
    const cache: Record<string, IDataProcessed> = {};
    const productionMachineCache: Record<number, IProductionMachineData> = {};

    productionData.forEach(production => {
      const millHeat = production.reference ?? "UNDEFINED";

      cache[millHeat] = {
        heatNumber: "",
        steelCode: "",
        millHeat,
        productionMachines: [],
        aggregate: {}
      };

      production.productionMachines.forEach(productionMachine => {
        const object = {
          productionMachine: productionMachine as IStrandProduction,
          billets: [],
          aggregate: {}
        };

        productionMachineCache[productionMachine.productionMachineID] = object;
        cache[millHeat].productionMachines.push(object);
        cache[millHeat].heatNumber = object.productionMachine.heatNumber;
        cache[millHeat].steelCode = object.productionMachine.steelCode;
      });
    });

    for (const billet of products) {
      const catalogName = billet.recipe ?? "UNDEFINED";
      const billetData: IBilletData = {
        ...billet,
        aggregateRecord: {}
      };

      let spec = specDictionary[catalogName];
      if (!spec) {
        const response = specs[catalogName];

        spec =  response && response.length ? response[0].content.fields : [];
        specDictionary[catalogName] = spec;
      }

      productionMachineCache[billet.productionMachineId].billets.push(billetData);

      billetData.aggregate.forEach(a => {
        const field = spec.find(f => f.name === fieldMapping[a.code]);
        const error = evalError(evalAvg(a.count, a.sum), field?.minimum ?? null, field?.maximum ?? null);
        const specMin = field?.minimum ?? null;
        const specMax = field?.maximum ?? null;

        if (!variableMapping[a.code]) {
          return;
        }

        billetData.aggregateRecord[variableMapping[a.code]] = {
          min: a.min,
          max: a.max,
          sum: a.sum,
          count: a.count,
          specMin,
          specMax,
          errors: error ? [ `Billet ${billet.billet}: ${error}` ] : []
        };

        updateAggregate(
          variableMapping[a.code],
          productionMachineCache[billet.productionMachineId].aggregate,
          billetData.aggregateRecord[variableMapping[a.code]]);

        updateAggregate(
          variableMapping[a.code],
          cache[billet.millHeat].aggregate,
          billetData.aggregateRecord[variableMapping[a.code]]);
      });
    }

    setDataProcessed(Object.values(cache));
  }, [ products, specs ]);

  const onExportData = () => {
    if (dataProcessed) {
      const files: Record<string, Blob> = {
        orders: exportFanByOrder(dataProcessed),
        machines: exportFanByMachine(dataProcessed),
        billets: exportFanByBillets(dataProcessed)
      };

      saveZipAs(files, "qr-rm.zip").then();
    }
  };

  const tableContainerHeight = parentSize.height - 48;

  return (
    <>
      <TableContainer className="flex-grow-1" style={{ maxHeight: tableContainerHeight, height: tableContainerHeight }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead style={{ backgroundColor: "#b0bec5" }}>
            <TableRow>
              <TableCell align="center" style={style00(1, 500, { zIndex: 999, position: "sticky", left: 0 })} colSpan={4}/>
              {columnList.map(c => <TableCell key={`header-l1-${c}`} align="center" style={styleByColumn[c](.5)} colSpan={5}>{c}</TableCell>)}
            </TableRow>
            <TableRow>
              <TableCell align="center" style={style00(1, 50, { left: 0 })}/>
              <TableCell align="center" style={style00(1, 150, { left: 50 })}>Order</TableCell>
              <TableCell align="center" style={style00(1, 150, { left: 200 })}>Heat Number</TableCell>
              <TableCell align="center" style={style00(1, 150, { left: 350 })}>Steel</TableCell>

              {columnList.map(c => {
                const keyBase = `header-l2-${c}`;
                const sx = styleByColumn[c](.5);

                return (
                  <Fragment key={keyBase}>
                    <TableCell key={`${keyBase}-min-spec`} align="center" style={sx}>MIN</TableCell>
                    <TableCell key={`${keyBase}-max-spec`} align="center" style={sx}>MAX</TableCell>
                    <TableCell key={`${keyBase}-min`} align="center" style={sx}>MIN</TableCell>
                    <TableCell key={`${keyBase}-max`} align="center" style={sx}>MAX</TableCell>
                    <TableCell key={`${keyBase}-avg`} align="center" style={sx}>AVG</TableCell>
                  </Fragment>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {(dataProcessed ?? []).map(row => <RowComponent key={`millHeat-row-${row.millHeat}`} row={row} />)}
          </TableBody>
        </Table>

        {!dataProcessed &&
          <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2">
            <CircularProgress className=""/>
          </Paper>
        }
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>
              <IconButton color="primary" onClick={onExportData} disabled={!dataProcessed || dataProcessed.length === 0}>
                <Tooltip title={"Export data"}>
                  <FileDownloadIcon/>
                </Tooltip>
              </IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default ProductionQRFanTable;
